import { useEffect, useState } from "react";
import { ShieldAlert, CheckCircle2, XCircle, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";

const when = (iso) => new Date(iso).toLocaleString("en-IN", { timeZone: "Asia/Kolkata", day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });

function EventRow({ e }) {
  return (
    <div className={cn("grid grid-cols-[auto_1fr] gap-3 rounded-xl border px-4 py-3 sm:grid-cols-[auto_1.2fr_1fr_1fr_auto] sm:items-center", e.ok ? "border-[#E7E2D6] bg-white" : "border-rose-200 bg-rose-50")} data-testid={`login-event-${e.id}`}>
      {e.ok ? <CheckCircle2 className="h-5 w-5 text-emerald-600" /> : <XCircle className="h-5 w-5 text-rose-600" />}
      <div className="min-w-0"><div className="truncate text-sm font-semibold">{e.name || e.email}</div><div className="truncate text-xs text-muted-foreground">{e.name ? e.email : "No such team member"}{e.role ? ` · ${e.role}` : ""}</div></div>
      <div className="col-start-2 text-xs sm:col-auto"><span className={e.ok ? "text-emerald-700" : "font-semibold text-rose-700"} data-testid={`login-event-status-${e.id}`}>{e.ok ? "Signed in" : `Failed: ${e.reason}`}</span></div>
      <div className="col-start-2 text-xs text-muted-foreground sm:col-auto" data-testid={`login-event-device-${e.id}`}>{e.device}{e.ip ? ` · ${e.ip}` : ""}</div>
      <div className="col-start-2 text-xs text-muted-foreground sm:col-auto sm:text-right">{when(e.at)}</div>
    </div>
  );
}

export function LoginActivity() {
  const [failed, setFailed] = useState(false);
  const [d, setD] = useState({ rows: [], failed_24h: 0 });
  const load = () => api.get("/auth/activity", { params: { failed, limit: 100 } }).then((r) => setD(r.data)).catch(() => {});
  useEffect(() => { load(); }, [failed]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <section className="mt-12" data-testid="login-activity">
      <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="font-display text-2xl font-bold text-[#0F382C]">Login activity</h2>
          <p className="text-sm text-muted-foreground">Who signed in, when and from which device. Failed attempts are shown in red.</p>
        </div>
        <div className="flex items-center gap-3">
          {d.failed_24h > 0 && <span className="inline-flex items-center gap-1 rounded-full bg-rose-100 px-3 py-1 text-xs font-semibold text-rose-700" data-testid="login-failed-24h"><ShieldAlert className="h-3.5 w-3.5" />{d.failed_24h} failed in 24 h</span>}
          <label className="flex items-center gap-2 text-sm"><Switch checked={failed} onCheckedChange={setFailed} data-testid="login-failed-only-switch" />Failed only</label>
          <Button variant="outline" size="icon" onClick={load} aria-label="Refresh" data-testid="login-activity-refresh"><RefreshCw className="h-4 w-4" /></Button>
        </div>
      </div>
      <div className="space-y-2">
        {d.rows.map((e) => <EventRow key={e.id} e={e} />)}
        {!d.rows.length && <div className="rounded-xl border border-dashed border-[#E7E2D6] p-6 text-center text-sm text-muted-foreground" data-testid="login-activity-empty">{failed ? "No failed sign-ins." : "No sign-ins recorded yet."}</div>}
      </div>
    </section>
  );
}
